<template>
  <div>
    <Transition name="fade">
      <AppModal
        v-show="showPhotoModal && !showCustomizationModal && !showCartModal"
        class="photo-modal"
        @mousedown="handleModalOutsideClick"
        @close="close"
      >
        <PhotoPreview :photo-url="photo.thumbnail.url" />

        <!-- Actions -->
        <section class="actions">
          <AppButton
            class="hidden justify-end btn-solid btn-secondary rounded-full my-4 mr-4 aspect-square"
            screen-reader-text="Select Option"
            @click="showOptions = !showOptions"
          >
            <FontAwesomeIcon
              :icon="faXmark"
              size="3x"
              class="duration-150"
              :class="{'-rotate-45': !showOptions}"
            />
          </AppButton>

          <!--STANDARD-->
          <PhotoActionBlock title="Standard">
            <template #description>
              {{ standardPhotoDescription }}
              <span class="text-gray-500">| Digital download</span>
            </template>

            <template v-if="event.isSponsored">
              <a
                :href="photo?.sponsoredPhoto"
                target="_blank"
                class="btn btn-solid btn-secondary btn-download"
                @click="close"
              >
                <FontAwesomeIcon
                  v-if="!isDownloading"
                  :icon="faArrowDownToLine"
                />
                Download now
              </a>
            </template>

            <template v-else>
              <AppButton
                :disabled="isPhotoInCart"
                :processing="addingPhotoToCart && !isPhotoInCart"
                class="btn-secondary btn-outline btn-add-to-cart"
                @click="handleAddToCart"
              >
                {{ isPhotoInCart ? 'Added to cart' : 'Add to cart' }}
              </AppButton>
            </template>
          </PhotoActionBlock>

          <!--PREMIUM-->
          <PhotoActionBlock title="Premium">
            <template #description>
              From R{{ event.prices[1].price.toFixed(2) }}
            </template>

            <AppButton
              class="btn-secondary btn-outline btn-add-to-cart"
              :disabled="addingPhotoToCart || addingPackToCart"
              @click="showCustomizationModal = true"
            >
              Customise
            </AppButton>
          </PhotoActionBlock>

          <!--PHOTO PACK-->
          <PhotoActionBlock
            v-if="searchedBibNumber && numberOfPhotos >= 8 && props.event.canPurchaseBulkPhotos"
            title="Photo Pack"
          >
            <template #description>
              R{{ photoPackPrice }} |
              <span class="text-gray-500">Pack of {{ numberOfPhotos }} photos of you (Bib No. {{
                searchedBibNumber
              }})</span>
            </template>

            <AppButton
              class="btn-secondary btn-outline btn-add-to-cart"
              :disabled="isPackInCart"
              :processing="addingPackToCart && !isPackInCart"
              @click="$emit('addPackToCartClicked')"
            >
              {{ isPackInCart ? 'Added to cart' : 'Add to cart' }}
            </AppButton>
          </PhotoActionBlock>
        </section>
      </AppModal>
    </Transition>

    <!-- Customise Photo Modal -->
    <Transition name="fade">
      <AppModal
        v-show="showCustomizationModal"
        id="customise-photo-modal"
        class="customize-photo-modal"
        :disabled="isSubmitting"
        size="xxl"
        @close="showCustomizationModal = false"
      >
        <CustomizePhotoForm
          v-model:is-submitting="isSubmitting"
          :photo="photo"
          :event="event"
          :searched-bib-number="searchedBibNumber"
          @confirm="showCartModal = true"
          @close="showCustomizationModal = false"
        />
      </AppModal>
    </Transition>

    <Transition name="fade">
      <CartModal
        v-show="showCartModal"
        @close="close(); showCartModal = false"
      />
    </Transition>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowDownToLine, faXmark } from '@fortawesome/pro-solid-svg-icons';

import { AppButton, AppModal } from '@/components/app';
import { CustomizePhotoForm } from '@/components/forms';

import { useCartStore } from '@/store';
import { DeliveryType, DigitalSize, PhotoType, StyleType } from '@/const';
import CartModal from '@/components/modals/CartModal.vue';
import { useDisableBodyScroll } from '@/composables';
import { PhotoActionBlock } from '@/components/blocks';
import { PhotoPreview } from '@/components/galleries';

const emit = defineEmits(['close', 'addToCartClicked', 'addPackToCartClicked']);

const cartStore = useCartStore();

const props = defineProps({
  photo: {
    type: Object,
    default: null,
  },
  event: {
    type: Object,
    default: null,
  },
  addingPhotoToCart: {
    type: Boolean,
    default: false,
  },
  addingPackToCart: {
    type: Boolean,
    default: false,
  },
  searchedBibNumber: {
    type: Number,
    default: null,
  },
  numberOfPhotos: {
    type: Number,
    default: null,
  },
  photoPackPrice: {
    type: Number,
    default: 0,
  },
});

const showPhotoModal = ref(false);
const showCustomizationModal = ref(false);
const showCartModal = ref(false);

const showOptions = ref(false);
const isSubmitting = ref(false);
const isDownloading = ref(false);

const handleAddToCart = () => {
  const data = {
    photo: props.photo.id,
  };

  emit('addToCartClicked', data);
  showCartModal.value = true;
};

const close = () => {
  if (event.target.classList.contains('cart-modal') || (!props.addingPhotoToCart && !props.addingPackToCart && !isDownloading.value)) {
    emit('close');
  }
};

const handleModalOutsideClick = () => {
  if (event.target.classList.contains('photo-modal')) {
    close();
  }
};

const getOrderItem = computed(() => {
  return cartStore.cart?.items?.find(order => order.photo?.ulid === props.photo.ulid && order.type === PhotoType.Single) || ({});
});

const isPhotoInCart = computed(() => {
  return cartStore.cart?.items?.some(order => order.photo?.ulid === props.photo.ulid && order.type === PhotoType.Single);
});

const isPackInCart = computed(() => {
  return cartStore.cart?.items?.some(order => order.bibNumber === props.searchedBibNumber);
});

const standardPhotoDescription = computed(() => {
  return props.event.isSponsored ? 'Free Download' : 'R' + (props.event.prices.find(price => price.style === StyleType.Event && price.size === DigitalSize.Standard && price.delivery === DeliveryType.Digital).price).toFixed(2);
});

const isScrollDisabled = ref(false);

onMounted(() => {
  showPhotoModal.value = true;
  useDisableBodyScroll(isScrollDisabled);
  isScrollDisabled.value = true;

});

const container = ref(null);
const parentHeight = ref(0);

onMounted(() => {
  if (container.value) {
    parentHeight.value = container.value.clientHeight;
  }
});

const figureStyles = computed(() => {
  if (!props.photo?.thumbnail?.dimensions || !parentHeight.value) {
    return {};
  }

  const { width, height } = props.photo.thumbnail.dimensions;
  const maxHeight = parentHeight.value;

  if (height > maxHeight) {
    const aspectRatio = width / height;
    const newWidth = maxHeight * aspectRatio;
    return {
      height: `${maxHeight}px`,
      width: `${newWidth}px`,
    };
  } else {
    return {
      width: `${width}px`,
      height: `${height}px`,
    };
  }
});

</script>

<style scoped>
.v-enter-active,
.v-leave-active {
  transition: height 0.3s ease;
}

.v-enter-from,
.v-leave-to {
  height: 0;
}
</style>
